import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Rating from '@mui/material/Rating';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { BreadcrumbHeader, StatusBadge, EmptyState, ActionMenu, ConfirmModal } from '../../components/shared';
import { suppliers, parts, supplierQuotes, quoteHistory } from '../../services/dataClient';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

// ─── Helpers ──────────────────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";const fmt = (n) =>
new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${m}/${day}/${y}`;
};

// ─── Lead time chip ───────────────────────────────────────────────────────────
function LeadTimeChip({ days }) {
  if (!days) return <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>;
  let bg, color;
  if (days < 5) {bg = '#E8F5E9';color = '#2E7D32';} else
  if (days <= 10) {bg = '#FFF8E1';color = '#E65100';} else
  {bg = '#FFEBEE';color = '#B71C1C';}

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '2px', borderRadius: '10px', backgroundColor: bg }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color, lineHeight: 1 }}>
        {days} day{days !== 1 ? 's' : ''}
      </Typography>
    </Box>);

}

// ─── Computed rating from quote history ───────────────────────────────────────
function computeRating(supplierName) {
  const terminal = [
  ...quoteHistory.filter((q) => q.supplierName === supplierName && (q.status === 'Accepted' || q.status === 'Declined')),
  ...supplierQuotes.filter((q) => q.supplierName === supplierName && (q.status === 'Accepted' || q.status === 'Declined'))];

  if (terminal.length === 0) return null;
  const accepted = terminal.filter((q) => q.status === 'Accepted').length;
  const raw = 1 + accepted / terminal.length * 4;
  return Math.round(raw * 2) / 2; // nearest 0.5
}

// ─── Table style constants ────────────────────────────────────────────────────
const TH = {
  backgroundColor: 'var(--secondary-light)',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: 'var(--grey-600)',
  textTransform: 'none',
  letterSpacing: 'normal',
  px: 2,
  py: '10px',
  borderBottom: '1px solid var(--grey-200)',
  whiteSpace: 'nowrap'
};

const TD = {
  px: 2,
  py: 0,
  border: 'none',
  fontSize: '0.875rem',
  color: 'var(--grey-800)',
  height: 44
};

const OEM_OPTIONS = ['Broadcom', 'Cisco', 'Dell', 'HPE', 'IBM', 'Juniper', 'Lenovo', 'NetApp', 'Pure Storage'];

// ─── Sales Rep Modal ──────────────────────────────────────────────────────────
function SalesRepModal({ open, mode, form, onChange, onClose, onSave }) {
  const canSave = form.oem && form.name && form.email;
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {if (reason !== 'backdropClick') onClose();}}
      maxWidth="xs"
      fullWidth>
      
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {mode === 'add' ? 'Add Sales Rep' : 'Edit Sales Rep'}
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        <FormControl size="small" required fullWidth sx={fieldSx}>
          <InputLabel>OEM</InputLabel>
          <Select
            label="OEM"
            value={form.oem}
            onChange={(e) => onChange('oem', e.target.value)}>
            
            {OEM_OPTIONS.map((o) =>
            <MenuItem key={o} value={o}>{o}</MenuItem>
            )}
          </Select>
        </FormControl>
        <TextField
          size="small" label="Name" value={form.name} required fullWidth sx={fieldSx}
          onChange={(e) => onChange('name', e.target.value)} />
        
        <TextField
          size="small" label="Email" type="email" value={form.email} required fullWidth sx={fieldSx}
          onChange={(e) => onChange('email', e.target.value)} />
        
        <TextField
          size="small" label="Phone (optional)" value={form.phone} fullWidth sx={fieldSx}
          onChange={(e) => onChange('phone', e.target.value)} />
        
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!canSave}
          onClick={onSave}
          sx={{ backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: 'none',
            px: 2
          }}>
          
          {mode === 'add' ? 'Add Rep' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function SupplierDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [catalogSearch, setCatalogSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [banner, setBanner] = useState('');

  // Local override so edits are reflected immediately without a full page reload
  const [localOverride, setLocalOverride] = useState(null);

  // Sales rep CRUD state
  const [repModalOpen, setRepModalOpen] = useState(false);
  const [repModalMode, setRepModalMode] = useState('add');
  const [repForm, setRepForm] = useState({ id: '', oem: '', name: '', email: '', phone: '' });
  const [deleteRepTarget, setDeleteRepTarget] = useState(null);

  const baseSupplier = suppliers.find((s) => s.id === id);
  const supplier = localOverride ?? baseSupplier;

  // ── Derive parts table from quote sources ────────────────────────────────────
  const quoteParts = useMemo(() => {
    if (!supplier) return [];

    // All priced quotes for this supplier (active + history)
    const priced = [
    ...quoteHistory.filter((q) => q.supplierName === supplier.name),
    ...supplierQuotes.filter((q) => q.supplierName === supplier.name && q.unitCost !== null)];

    // Group by partNumber, keep most recent per part
    const byPart = {};
    for (const q of priced) {
      const existing = byPart[q.partNumber];
      if (!existing || (q.responseDate ?? '') > (existing.responseDate ?? '')) {
        byPart[q.partNumber] = q;
      }
    }

    return Object.values(byPart)
    .sort((a, b) => (b.responseDate ?? '').localeCompare(a.responseDate ?? ''))
    .map((q) => {
      const partRec = parts.find((p) => p.partNumber === q.partNumber);
      return { ...q, description: partRec?.description ?? '—', partId: partRec?.id ?? null };
    });
  }, [supplier]);

  // ── Computed rating ──────────────────────────────────────────────────────────
  const computedRating = useMemo(
    () => supplier ? computeRating(supplier.name) : null,
    [supplier]
  );

  const filteredParts = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    if (!q) return quoteParts;
    return quoteParts.filter(
      (e) =>
      e.partNumber.toLowerCase().includes(q) ||
      e.description.toLowerCase().includes(q)
    );
  }, [quoteParts, catalogSearch]);

  const openEdit = () => {
    if (!supplier) return;
    setEditForm({
      name: supplier.name,
      contactName: supplier.contactName,
      contactEmail: supplier.contactEmail,
      phone: supplier.phone ?? '',
      address: supplier.address ?? '',
      city: supplier.city ?? '',
      state: supplier.state ?? '',
      zip: supplier.zip ?? '',
      avgLeadTimeDays: String(supplier.avgLeadTimeDays),
      notes: supplier.notes ?? '',
      active: supplier.active
    });
    setEditOpen(true);
  };

  const handleEditSave = () => {
    const updated = { ...supplier, ...editForm, avgLeadTimeDays: Number(editForm.avgLeadTimeDays) };
    // Patch the module-level array so the list page reflects the change
    const idx = suppliers.findIndex((s) => s.id === id);
    if (idx !== -1) Object.assign(suppliers[idx], updated);
    setLocalOverride(updated);
    setEditOpen(false);
    setBanner('Supplier updated successfully.');
    setTimeout(() => setBanner(''), 4000);
  };

  const setField = (field) => (e) =>
  setEditForm((prev) => ({ ...prev, [field]: e.target.value }));

  const openAddRep = () => {
    setRepForm({ id: '', oem: '', name: '', email: '', phone: '' });
    setRepModalMode('add');
    setRepModalOpen(true);
  };

  const openEditRep = (rep) => {
    setRepForm({ id: rep.id, oem: rep.oem, name: rep.name, email: rep.email, phone: rep.phone ?? '' });
    setRepModalMode('edit');
    setRepModalOpen(true);
  };

  const handleRepSave = () => {
    const idx = suppliers.findIndex((s) => s.id === id);
    if (idx === -1) return;
    let updatedReps;
    if (repModalMode === 'add') {
      const newRep = { ...repForm, id: `rep-${Math.random().toString(36).slice(2, 7)}` };
      updatedReps = [...(supplier.salesReps ?? []), newRep];
    } else {
      updatedReps = (supplier.salesReps ?? []).map((r) => r.id === repForm.id ? { ...repForm } : r);
    }
    suppliers[idx].salesReps = updatedReps;
    setLocalOverride({ ...supplier, salesReps: updatedReps });
    setRepModalOpen(false);
  };

  const handleRepDelete = () => {
    if (!deleteRepTarget) return;
    const idx = suppliers.findIndex((s) => s.id === id);
    if (idx === -1) return;
    const updatedReps = (supplier.salesReps ?? []).filter((r) => r.id !== deleteRepTarget.id);
    suppliers[idx].salesReps = updatedReps;
    setLocalOverride({ ...supplier, salesReps: updatedReps });
    setDeleteRepTarget(null);
  };

  const isFormValid =
  editForm?.name &&
  editForm?.contactName &&
  editForm?.contactEmail &&
  editForm?.address &&
  editForm?.city &&
  editForm?.state &&
  editForm?.zip &&
  editForm?.avgLeadTimeDays &&
  !isNaN(Number(editForm?.avgLeadTimeDays));

  if (!supplier) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/inventory/suppliers')}
          sx={{ textTransform: 'none', color: 'var(--grey-600)', mb: 2, fontSize: '0.875rem' }}>
          
          Back to Suppliers
        </Button>
        <EmptyState title="Supplier not found" description="This supplier does not exist or has been removed." />
      </Box>);

  }

  const infoFields = [
  { label: 'Supplier Code', value: supplier.code },
  { label: 'Contact Name', value: supplier.contactName },
  { label: 'Contact Email', value:
    <Typography
      component="a"
      href={`mailto:${supplier.contactEmail}`}
      sx={{ fontSize: '0.875rem', color: '#009688', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
      
          {supplier.contactEmail}
        </Typography>

  },
  { label: 'Phone', value: supplier.phone || '—' },
  { label: 'Address', value: `${supplier.address}, ${supplier.city}, ${supplier.state}` },
  { label: 'Avg Lead Time', value: <LeadTimeChip days={supplier.avgLeadTimeDays} /> },
  { label: 'Rating', value: computedRating !== null ?
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Rating value={computedRating} precision={0.5} readOnly size="small" sx={{ '& .MuiRating-iconFilled': { color: '#009688' } }} />
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-500)' }}>{computedRating.toFixed(1)}</Typography>
        </Box> :
    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>No quote history</Typography>
  },
  { label: 'Status', value: <StatusBadge status={supplier.active ? 'Active' : 'Inactive'} /> }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Success banner */}
      {banner &&
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
          borderRadius: '8px', px: 2, py: '12px'
        }}>
        
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            {banner}
          </Typography>
        </Box>
      }

      {/* Page header */}
      <BreadcrumbHeader
        title={supplier.name}
        subtitle={supplier.code}
        crumbs={[
        { label: 'Inventory' },
        { label: 'Suppliers', path: '/inventory/suppliers' },
        { label: supplier.name }]
        } />

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

        {/* Left — Quote History Parts */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ContentCard
            sx={{ p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
                  Parts Quoted ({quoteParts.length})
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '2px' }}>
                  Last bid price per part across all RFQ activity
                </Typography>
              </Box>
              <TextField
                size="small"
                placeholder="Search parts…"
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                InputProps={{
                  startAdornment:
                  <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: 'var(--grey-400)' }} />
                    </InputAdornment>

                }}
                sx={{ width: 200,
                  '& .MuiInputBase-root': { fontSize: '0.8125rem', height: 32 },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'var(--grey-200)' },
                    '&.Mui-focused fieldset': { borderColor: '#009688' }
                  }
                }} />
              
            </Box>

            <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
              {quoteParts.length === 0 ?
              <Box sx={{ px: 3, py: 3 }}>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic' }}>
                    No RFQ activity found for this supplier.
                  </Typography>
                </Box> :

              <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Part #', 'Description', 'Last Bid', 'Lead Time', 'Last Response', 'Status'].map((h) =>
                    <TableCell key={h} sx={{ ...TH, py: '8px' }}>{h}</TableCell>
                    )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredParts.length === 0 ?
                  <TableRow>
                        <TableCell colSpan={6} sx={{ ...TD, textAlign: 'center', color: 'var(--grey-400)', fontStyle: 'italic' }}>
                          No parts match your search.
                        </TableCell>
                      </TableRow> :

                  filteredParts.map((entry) =>
                  <TableRow
                    key={entry.id}
                    sx={{ '&:not(:last-child) td': { borderBottom: '1px solid var(--grey-200)' } }}>
                    
                          <TableCell sx={TD}>
                            {entry.partId ?
                      <Typography
                        onClick={() => navigate(`/inventory/search/${entry.partId}`)}
                        sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                        
                                {entry.partNumber}
                              </Typography> :

                      <Typography sx={{ fontSize: '0.875rem' }}>{entry.partNumber}</Typography>
                      }
                          </TableCell>
                          <TableCell sx={TD}>{entry.description}</TableCell>
                          <TableCell sx={{ ...TD, fontWeight: 600 }}>{fmt(entry.unitCost)}</TableCell>
                          <TableCell sx={TD}>
                            <LeadTimeChip days={entry.leadTimeDays} />
                          </TableCell>
                          <TableCell sx={{ ...TD, color: 'var(--grey-600)' }}>
                            {fmtDate(entry.responseDate)}
                          </TableCell>
                          <TableCell sx={TD}>
                            <StatusBadge status={entry.status} />
                          </TableCell>
                        </TableRow>
                  )
                  }
                  </TableBody>
                </Table>
              }
            </Box>
          </ContentCard>

          {/* Sales Reps card */}
          <ContentCard sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: '12px', borderBottom: '1px solid var(--grey-200)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
                Sales Reps
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={openAddRep}
                sx={{ textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  color: '#009688',
                  '&:hover': { backgroundColor: '#E0F2F1' },
                  minWidth: 0,
                  px: 1
                }}>
                
                Add Rep
              </Button>
            </Box>

            {!supplier.salesReps || supplier.salesReps.length === 0 ?
            <Box sx={{ px: 3, py: 3, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic' }}>
                  No sales reps added yet.
                </Typography>
              </Box> :

            <Box sx={{ m: 2, border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['OEM', 'Name', 'Email', 'Phone', ''].map((h) =>
                    <TableCell key={h} sx={{ ...TH, py: '8px' }}>{h}</TableCell>
                    )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {supplier.salesReps.map((rep) =>
                  <TableRow key={rep.id} sx={{ '&:not(:last-child) td': { borderBottom: '1px solid var(--grey-200)' } }}>
                        <TableCell sx={TD}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '7px', py: '2px', borderRadius: '4px', backgroundColor: '#E0F2F1' }}>
                            <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, color: '#00695C', letterSpacing: '0.03em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                              {rep.oem}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...TD, fontWeight: 600 }}>{rep.name}</TableCell>
                        <TableCell sx={TD}>
                          <Typography
                        component="a"
                        href={`mailto:${rep.email}`}
                        sx={{ fontSize: '0.875rem', color: '#009688', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        
                            {rep.email}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...TD, color: 'var(--grey-600)' }}>{rep.phone || '—'}</TableCell>
                        <TableCell sx={{ ...TD, pr: 1 }}>
                          <ActionMenu
                        actions={[
                        { label: 'Edit', onClick: () => openEditRep(rep) },
                        { label: 'Remove', onClick: () => setDeleteRepTarget(rep) }]
                        } />
                      
                        </TableCell>
                      </TableRow>
                  )}
                  </TableBody>
                </Table>
              </Box>
            }
          </ContentCard>
        </Box>

        {/* Right — Supplier Info */}
        <Box sx={{ width: 340, flexShrink: 0, alignSelf: 'flex-start' }}>
          <ContentCard sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid var(--grey-200)' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
                Supplier Info
              </Typography>
            </Box>
            <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {infoFields.map((field) =>
              <Box key={field.label} sx={{ display: 'flex', gap: 2 }}>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', minWidth: 100, flexShrink: 0 }}>
                    {field.label}
                  </Typography>
                  <Typography component="div" sx={{ fontSize: '0.875rem', color: 'var(--grey-900)', flex: 1 }}>
                    {field.value ?? '—'}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ px: 3, py: 2, borderTop: '1px solid var(--grey-200)' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={openEdit}
                fullWidth
                sx={{ textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderColor: 'var(--grey-300)',
                  color: 'var(--grey-700)',
                  '&:hover': { borderColor: 'var(--grey-400)', backgroundColor: 'var(--grey-50)' },
                  boxShadow: 'none'
                }}>
                
                Edit Supplier
              </Button>
            </Box>
          </ContentCard>
        </Box>
      </Box>

      {/* Sales Rep modal */}
      <SalesRepModal
        open={repModalOpen}
        mode={repModalMode}
        form={repForm}
        onChange={(field, value) => setRepForm((prev) => ({ ...prev, [field]: value }))}
        onClose={() => setRepModalOpen(false)}
        onSave={handleRepSave} />

      {/* Delete rep confirm */}
      <ConfirmModal
        open={Boolean(deleteRepTarget)}
        title="Remove Sales Rep"
        message={`Remove ${deleteRepTarget?.name ?? 'this rep'} from the sales rep list?`}
        confirmLabel="Remove"
        onConfirm={handleRepDelete}
        onCancel={() => setDeleteRepTarget(null)} />

      {/* Edit Supplier modal */}
      {editForm &&
      <Dialog
        open={editOpen}
        onClose={(_, reason) => {if (reason !== 'backdropClick') setEditOpen(false);}}
        maxWidth="sm"
        fullWidth>
        
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Edit Supplier
            <IconButton size="small" onClick={() => setEditOpen(false)} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
              <HighlightOffIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
            <TextField
            size="small" label="Supplier Name" value={editForm.name} onChange={setField('name')}
            fullWidth required sx={fieldSx} />
          
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
              size="small" label="Contact Name" value={editForm.contactName} onChange={setField('contactName')}
              fullWidth required sx={fieldSx} />
            
              <TextField
              size="small" label="Contact Email" type="email" value={editForm.contactEmail} onChange={setField('contactEmail')}
              fullWidth required sx={fieldSx} />
            
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
              size="small" label="Phone (optional)" value={editForm.phone} onChange={setField('phone')}
              fullWidth sx={fieldSx} />
            
              <TextField
              size="small" label="Avg Lead Time (days)" value={editForm.avgLeadTimeDays} onChange={setField('avgLeadTimeDays')}
              sx={{ width: 180, ...fieldSx }} required
              inputProps={{ inputMode: 'numeric' }} />
            
            </Box>
            <TextField
            size="small" label="Address" value={editForm.address} onChange={setField('address')}
            fullWidth required sx={fieldSx} />
          
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
              size="small" label="City" value={editForm.city} onChange={setField('city')}
              fullWidth required sx={fieldSx} />
            
              <TextField
              size="small" label="State" value={editForm.state} onChange={setField('state')}
              sx={{ width: 100, ...fieldSx }} required />
            
              <TextField
              size="small" label="Zip Code" value={editForm.zip} onChange={setField('zip')}
              sx={{ width: 120, ...fieldSx }} required />
            
            </Box>
            <TextField
            size="small" label="Notes (optional)" value={editForm.notes} onChange={setField('notes')}
            multiline rows={2} fullWidth sx={fieldSx} />
          
            {/* Status toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>Status</Typography>
              <ToggleButtonGroup
              exclusive
              size="small"
              value={editForm.active ? 'active' : 'inactive'}
              onChange={(_, val) => {if (val) setEditForm((prev) => ({ ...prev, active: val === 'active' }));}}
              sx={{ '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  px: 2,
                  py: '4px',
                  borderColor: 'var(--grey-200)',
                  color: 'var(--grey-500)',
                  '&.Mui-selected': { backgroundColor: '#009688', color: '#fff', borderColor: '#009688' },
                  '&.Mui-selected:hover': { backgroundColor: '#00796B' }
                }
              }}>
              
                <ToggleButton value="active">Active</ToggleButton>
                <ToggleButton value="inactive">Inactive</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Typography
            onClick={() => setEditOpen(false)}
            sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
            
              Cancel
            </Typography>
            <Button
            variant="contained"
            size="small"
            disabled={!isFormValid}
            onClick={handleEditSave}
            sx={{ backgroundColor: '#009688',
              '&:hover': { backgroundColor: '#00796B' },
              '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: 'none',
              px: 2
            }}>
            
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      }
    </Box>);

}